<template>
  <div>
    <SubmitalNavbar />

    <SubmitalHeader :submital="submital" page="fotos" />

    <ContentView v-if="submital.id" class="mt-3">
      <b-row  class="justify-content-center mx-0">
        <b-col>
          <div v-if="requiredPhotoKeys.length > 0">
            <p class="configurator-label">Fotos requeridas ({{ requiredPhotoKeys.length }})</p>
            <b-row>
              <div id="photo-list">
                <div v-for="caption in requiredPhotoKeys" :key="caption">
                  <template v-if="getPhotoByLabel(caption)">
                    <PhotoCard
                      :photo="getPhotoByLabel(caption)"
                      :editable="false"
                      :modalTitle="getPhotoTitle(caption)"
                      :actionable="!submitalLocked"
                      @photo-updated="updatePhoto"
                      @photo-deleted="deletePhoto"
                    />
                  </template>
                  <template v-else>
                    <div>
                      <ImageUploader
                        :target="'uppy-target-for-' + caption"
                        :width="270"
                        :show-remove-button="false"
                        :reset-after-upload="true"
                        :label="caption"
                        :disabled="submitalLocked"
                        @photo-added="createPhoto"
                        @preview-generated="storePreview"
                      />
                      <PhotoLabel
                        :label="requiredPhotosMap[caption]"
                        :caption="caption"
                        :source="configurableTypeUrlString"
                        :modal-required="true"
                      />
                    </div>
                  </template>
                </div>
              </div>
            </b-row>
          </div>
          <div>
            <p class="configurator-label">{{ photosLabel }}</p>
            <b-row>
              <div id="photo-list">
                <ImageUploader
                  target="uppy-target-other"
                  :width="270"
                  :showRemoveButton="false"
                  :resetAfterUpload="true"
                  :disabled="submitalLocked"
                  @photo-added="createPhoto"
                  @preview-generated="storePreview"
                />
                <PhotoCard
                  v-for="photo in otherPhotos"
                  :photo="photo"
                  :key="photo.id"
                  :modalTitle="submital.submital_no + (photo.caption ? ` | ${photo.caption}` : '')"
                  :actionable="!submitalLocked"
                  @photo-updated="updatePhoto"
                  @photo-deleted="deletePhoto"
                />
              </div>
            </b-row>
          </div>
        </b-col>
      </b-row>
    </ContentView>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { normalize, schema } from 'normalizr'
import ContentView from '../../shared/ContentView'
import ImageUploader from '../../shared/ImageUploader'
import PhotoCard from '../../shared/PhotoCard'
import SubmitalHeader from './SubmitalHeader'
import SubmitalNavbar from './SubmitalNavbar'
import PhotoLabel from './PhotoLabel'

export default {
  name: 'SubmitalPhotos',
  components: { ContentView, ImageUploader, PhotoCard, SubmitalHeader, SubmitalNavbar, PhotoLabel },
  data () {
    return {
      id: Number(this.$route.params.id),
      photoData: {},
      photoList: [],
      storedPreview: null,
      loading: false,
      processing: false
    }
  },
  computed: {
    ...mapState ({ submital: state => state.configurator.submital }),
    ...mapGetters ([
      'configurableTypeUrlString',
      'requiredPhotosMap',
      'requiredPhotoKeys',
      'submitalLocked'
    ]),
    photos () {
      return this.photoList
        .map(id => this.photoData[id])
        .filter(Boolean)
        .sort((a, b) => a.id - b.id)
    },
    requiredPhotos () {
      return this.photos
        .filter(photo => this.requiredPhotosMap[photo.caption])
        .map(photo => ({
          ...photo,
          label_key: photo.caption,
          caption: this.requiredPhotosMap[photo.caption],
        }))
    },
    otherPhotos () {
      return this.photos
                 .filter(photo => !this.requiredPhotosMap[photo.caption])
    },
    photosLabel () {
      if (this.requiredPhotoKeys.length > 0) {
        return 'Otras fotos'
      }
      return 'Fotos'
    }
  },
  mounted () {
    if (this.submital.id && this.submital.id === this.id) {
      this.fetchPhotos()
    } else {
      this.fetchSubmital()
    }
  },
  methods: {
    fetchSubmital () {
      this.$http.get(`/api/production/submitals/${this.id}`)
        .then((res) => {
          const { configurable, _quotes, ...submital } = res.data
          this.$store.commit('setConfigurable', configurable)
          this.$store.commit('setSubmital', submital)
          this.fetchPhotos()
        })
        .catch(e => {
          // console.log('error',e)
          this.$store.commit('setConfigurable', {})
          this.$store.commit('setSubmital', {})
        })
    },
    fetchPhotos () {
      this.$http.get(`/api/production/submitals/${this.submital.id}/photos`)
        .then((res) => {
          // console.log(res.data)
          const data = normalize(
            { photos: res.data },
            { photos: [ new schema.Entity('photos') ] }
          )
          if (data.entities.hasOwnProperty('photos')) {
            this.photoData = data.entities.photos
          }
          this.photoList = data.result.photos
          this.loading = false
        })
        .catch(e => console.log(e))
    },
    storePreview (preview) {
      this.storedPreview = preview
    },
    createPhoto (form) {
      if (this.processing) { return }
      this.processing = true
      this.$http.post(`/api/production/submitals/${this.submital.id}/photos`, {
        photo: {
          image: form.image,
          caption: form.caption
        }
      })
        .then((res) => {
          const createdPhoto = res.data.photo
          this.$set(this.photoData, createdPhoto.id, createdPhoto)
          this.photoList.push(createdPhoto.id)
          this.$store.commit('setSubmital', res.data.submital)

          this.$nextTick(() => {
            const anchors = document.getElementsByClassName('preview-anchor-' + createdPhoto.id)
            // console.log(anchors)
            Array.from(anchors).forEach(anc => {
              const img = document.createElement('img')
              img.src = this.storedPreview
              img.style.width = '238px'
              img.style.objectFit = 'contain'
              img.style.backgroundColor = '#fff'
              anc.appendChild(img)
            })
          })
        })
        .catch(e => console.log(e))
        .finally(this.processing = false)
    },
    updatePhoto (obj) {
      if (this.processing) { return }
      this.processing = true
      this.$http.patch(`/api/production/submitals/${this.submital.id}/photos/${obj.photo.id}`, {
        photo: obj.form
      })
        .then((res) => {
          // console.log(res)
          this.$set(this.photoData, res.data.id, res.data)
        })
        .catch(e => console.log(e))
        .finally(this.processing = false)
    },
    deletePhoto (photo) {
      if (this.processing) { return }
      this.processing = true
      this.$http.delete(`/api/production/submitals/${this.submital.id}/photos/${photo.id}`)
        .then((res) => {
          // console.log(res)
          delete this.photoData[res.data.photo.id]
          this.photoList = this.photoList.filter(id => id !== res.data.photo.id)
          this.$store.commit('setSubmital', res.data.submital)
        })
        .catch(e => console.log(e))
        .finally(this.processing = false)
    },
    getPhotoByLabel (caption) {
      return this.requiredPhotos.find(photo => photo.label_key === caption)
    },
    getPhotoTitle (caption) {
      const photo = this.getPhotoByLabel(caption)
      if (!photo) { return 'Detalles de foto' }

      return `${this.submital.submital_no} | ${photo.caption}`
    }
  }

}
</script>

<style scoped>
#photo-list {
  display: grid;
  justify-content: center;
  width: 100%;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: 1rem;
}

#photo-list {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  justify-items: center;
  padding: 1rem;
}

@media (max-width: 992px) {
  #photo-list {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  #photo-list {
    grid-template-columns: repeat(1, 1fr);
  }
}
</style>
